import { useSnackbar } from 'notistack';

/**
 * Custom hook for consistent notification handling
 * Wraps notistack's useSnackbar with simplified API
 * 
 * @returns {Object} Notification methods
 */
const useNotification = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  /**
   * Show success notification
   * @param {string} message - Success message
   * @param {Object} options - Additional options
   */
  const showSuccess = (message, options = {}) => {
    return enqueueSnackbar(message, {
      variant: 'success',
      autoHideDuration: 4000,
      ...options,
    });
  };

  /**
   * Show error notification
   * @param {string} message - Error message
   * @param {Object} options - Additional options
   */
  const showError = (message, options = {}) => {
    return enqueueSnackbar(message, {
      variant: 'error',
      autoHideDuration: 6000,
      ...options,
    });
  };

  /**
   * Show warning notification
   * @param {string} message - Warning message
   * @param {Object} options - Additional options
   */
  const showWarning = (message, options = {}) => {
    return enqueueSnackbar(message, {
      variant: 'warning',
      autoHideDuration: 5000,
      ...options,
    });
  };

  /**
   * Show info notification
   * @param {string} message - Info message
   * @param {Object} options - Additional options
   */
  const showInfo = (message, options = {}) => {
    return enqueueSnackbar(message, {
      variant: 'info',
      autoHideDuration: 4000,
      ...options,
    });
  };

  /**
   * Close notification by key
   * @param {string|number} key - Notification key
   */
  const close = (key) => {
    closeSnackbar(key);
  };

  /**
   * Close all notifications
   */
  const closeAll = () => {
    closeSnackbar();
  };

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    close,
    closeAll,
  };
};

export default useNotification;
